import { AsyncPipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { authBackendEndPointToken } from '../../app.config';
import { AuthService } from '../auth/auth.service';
import { OrganizationMembership } from '../auth/membership.model';
import { RoleDisplayNamePipe } from '../role-display-name.pipe';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
interface MyRequest { organizationId: string; status: string; requestedAt: string; }
interface OrgOption { id: string; name: string; }

@Component({
  selector: 'app-my-hospitals',
  imports: [AsyncPipe, FormsModule,MatInputModule,MatAutocompleteModule, MatButtonModule, MatFormFieldModule, MatIconModule, MatSelectModule, RoleDisplayNamePipe],
  template: `
    @if (message) {
      <div class="msg" [class.err]="isError" role="status">{{ message }}</div>
    }

    <h3>Hospitals you belong to</h3>
    @for (m of memberships(); track m.organizationId) {
      <div class="row">
        <mat-icon>domain</mat-icon>
        <div class="grow">
          <div class="name">
            {{ m.organizationName || 'Hospital' }}
            @if (m.isActive) { <span class="badge">Current</span> }
          </div>
          <div class="roles">
            @for (r of m.roles; track r) { <span class="chip">{{ r | roleDisplayName }}</span> }
          </div>
        </div>
        <button mat-stroked-button color="warn"
                [disabled]="m.isActive || memberships().length < 2 || busy"
                [title]="m.isActive ? 'Switch to another hospital to leave this one' : ''"
                (click)="leave(m)">
          Leave
        </button>
      </div>
    } @empty {
      <p>No hospitals yet.</p>
    }

   @if (pending().length) {
  <h3>Requests and invitations</h3>
  @for (r of pending(); track r.organizationId) {
    <div class="row">
      <mat-icon>{{ r.status === 'Invited' ? 'mail' : 'hourglass_empty' }}</mat-icon>
      <div class="grow">
        <div class="name">{{ nameOf(r.organizationId) }}</div>
        <div class="roles">{{ r.status === 'Invited' ? 'Invitation to join' : r.status }}</div>
      </div>
      @if (r.status === 'Invited') {
        <button mat-raised-button color="primary" [disabled]="busy" (click)="acceptInvite(r)">Accept</button>
        <button mat-stroked-button [disabled]="busy" (click)="declineInvite(r)">Decline</button>
      }
    </div>
  }
}

   <h3>Request to join another hospital</h3>
<div class="join">
  <mat-form-field appearance="outline" class="grow">
    <mat-label>Hospital</mat-label>
    <input matInput
           type="text"
           placeholder="Search hospitals..."
           [ngModel]="searchValue"
           (ngModelChange)="onSearchChange($event)"
           [matAutocomplete]="auto" />
    <mat-autocomplete #auto="matAutocomplete" [displayWith]="displayOrg">
      @for (o of filteredOrgs(); track o.id) {
        <mat-option [value]="o">{{ o.name }}</mat-option>
      } @empty {
        <mat-option disabled>No hospitals found</mat-option>
      }
    </mat-autocomplete>
  </mat-form-field>

  <button mat-raised-button color="primary" [disabled]="!selectedOrgId || busy" (click)="requestJoin()">
    Send request
  </button>
</div>
  `,
  styles: [`
    :host { display: block; padding: 16px 4px; }
    h3 { margin: 20px 0 8px; }
    .row { display: flex; align-items: center; gap: 12px; padding: 10px 12px; margin-bottom: 8px;
           border: 1px solid var(--mat-sys-outline-variant); border-radius: 12px; }
    .grow { flex: 1; min-width: 0; }
    .name { font-weight: 600; }
    .badge { margin-left: 8px; font-size: 11px; padding: 1px 8px; border-radius: 8px;
             background: var(--mat-sys-primary-container); color: var(--mat-sys-on-primary-container); }
    .roles { display: flex; gap: 4px; flex-wrap: wrap; font-size: 12px; margin-top: 2px; }
    .chip { padding: 0 6px; border-radius: 8px; background: var(--mat-sys-secondary-container);
            color: var(--mat-sys-on-secondary-container); }
    .join { display: flex; gap: 12px; align-items: baseline; }
    .msg { padding: 8px 12px; border-radius: 8px; margin-bottom: 8px;
           background: var(--mat-sys-secondary-container); }
    .msg.err { background: var(--mat-sys-error-container); color: var(--mat-sys-on-error-container); }
  `]
})
export class MyHospitalsComponent implements OnInit {
  private http = inject(HttpClient);
  private base = inject(authBackendEndPointToken);
  private auth = inject(AuthService);

  orgs: OrgOption[] = [];
  requests: MyRequest[] = [];
  selectedOrgId: string | null = null;
  busy = false;
  message: string | null = null;
  isError = false;

  ngOnInit(): void {
      this.auth.user.subscribe(u => {
    console.log('[MyHospitals] user changed:', u);
    console.log('[MyHospitals] memberships:', u?.memberships);
  });

    this.http.get<{ data: OrgOption[] }>(`${this.base}/api/auth/Organization?active=true&_sort=name`)
      .subscribe({ next: r => (this.orgs = r.data ?? []) });
    this.loadRequests();
  }
    searchValue: string | OrgOption = '';

// Shows the hospital name in the input after an option is picked
displayOrg = (o: OrgOption | string | null): string =>
  typeof o === 'string' ? o : o?.name ?? '';

onSearchChange(value: string | OrgOption): void {
  this.searchValue = value;
  // Typing text = nothing selected yet; picking an option gives an object
  this.selectedOrgId = typeof value === 'string' ? null : value.id;
}

filteredOrgs(): OrgOption[] {
  const term = (typeof this.searchValue === 'string'
    ? this.searchValue
    : this.searchValue?.name ?? '').trim().toLowerCase();
  const list = this.joinableOrgs();
  return term ? list.filter(o => o.name.toLowerCase().includes(term)) : list;
}

  memberships(): OrganizationMembership[] {
    return this.auth.user.getValue()?.memberships ?? [];
  }
acceptInvite(r: MyRequest): void {
  this.busy = true;
  this.http.post<{ message: string; organizationId: string; organizationName?: string; roles: string[] }>(
    `${this.base}/api/membership-requests/invitations/accept`, { organizationId: r.organizationId })
    .subscribe({
      next: res => {
        this.auth.addMembership({
          organizationId: res.organizationId,
          organizationName: res.organizationName,
          roles: res.roles,
          isActive: false
        });
        this.show(res.message, false);
        this.busy = false;
        this.loadRequests();
      },
      error: e => { this.show(e?.error?.message ?? 'Could not accept.', true); this.busy = false; }
    });
}

declineInvite(r: MyRequest): void {
  this.busy = true;
  this.http.post<{ message: string }>(
    `${this.base}/api/membership-requests/invitations/decline`, { organizationId: r.organizationId })
    .subscribe({
      next: res => { this.show(res.message, false); this.busy = false; this.loadRequests(); },
      error: e => { this.show(e?.error?.message ?? 'Could not decline.', true); this.busy = false; }
    });
}
  // Pending, plus anything inactive (suspended/archived) so the user knows why they can't rejoin
  pending(): MyRequest[] {
    return this.requests;
  }

  joinableOrgs(): OrgOption[] {
    const taken = new Set([
      ...this.memberships().map(m => m.organizationId),
      ...this.requests.map(r => r.organizationId)
    ]);
    return this.orgs.filter(o => !taken.has(o.id));
  }

  nameOf(id: string): string {
    return this.orgs.find(o => o.id === id)?.name ?? 'Hospital';
  }

  private loadRequests(): void {
    this.http.get<MyRequest[]>(`${this.base}/api/membership-requests/mine`)
      .subscribe({ next: r => (this.requests = r ?? []) });
  }

  requestJoin(): void {
    if (!this.selectedOrgId) return;
    this.busy = true;
    this.http.post<{ message: string }>(`${this.base}/api/membership-requests`, { organizationId: this.selectedOrgId })
      .subscribe({
        next: r => { this.show(r.message, false); this.selectedOrgId = null;this.searchValue = '';  this.busy = false; this.loadRequests(); },
        error: e => { this.show(e?.error?.message ?? 'Could not send request.', true); this.busy = false; }
      });
  }

  leave(m: OrganizationMembership): void {
    if (!confirm(`Leave ${m.organizationName || 'this hospital'}? An administrator will need to reactivate you to rejoin.`)) {
      return;
    }
    this.busy = true;
    this.http.post<{ message: string }>(`${this.base}/api/membership-requests/leave`, { organizationId: m.organizationId })
      .subscribe({
        next: r => { this.auth.removeMembership(m.organizationId); this.show(r.message, false); this.busy = false; this.loadRequests(); },
        error: e => { this.show(e?.error?.message ?? 'Could not leave.', true); this.busy = false; }
      });
  }

  private show(text: string, error: boolean): void {
    this.message = text;
    this.isError = error;
    setTimeout(() => (this.message = null), 6000);
  }
}